import { useState } from 'react';

import {
  AdminFonctionnelControllerApi,
  AutoriteFourriereControllerApi,
  DossierDtoAdminFonctionnel,
  DossierModificationViolationsDto,
  DossierResponseDto,
  ForceOrdreControllerApi,
  UpdateDonneesInterieurVehiculeRequestDto,
  UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import { UpdateDonnesVehiculeFormValues } from 'types/DonneesVehicule/UpdateDonnesVehiculeFormValues';

import { backAlertMessage } from './utils/backAlertMessage';

type SubmitFunction = {
  submit: (values: UpdateDonnesVehiculeFormValues) => Promise<void>;
  inProgress: boolean;
  validateUpdateDonneesVehicule: (
    field: UpdateDonneesInterieurVehiculeViolationDtoFormFieldEnum,
    dossierId: string,
    values: UpdateDonneesInterieurVehiculeRequestDto,
  ) => Promise<DossierModificationViolationsDto>;
  isAdminFonctionnel: boolean;
};

export type ControllerApi =
  | AdminFonctionnelControllerApi
  | ForceOrdreControllerApi
  | AutoriteFourriereControllerApi;

type SetDossierAdminF = (dossier: DossierDtoAdminFonctionnel) => void;
type SetDossierFo = (dossier: DossierResponseDto) => void;

export const buildUpdateDonneesVehiculeDto = (
  values: UpdateDonnesVehiculeFormValues,
  controller: ControllerApi,
): UpdateDonneesInterieurVehiculeRequestDto => {
  const isAdminFonctionnel = isInstanceOfAdminFonctionnel(controller);

  return {
    newSansPlaque: values.sansPlaque ?? null,
    newMotifEntreeIdCorrelation: values.idCorrelationMotifMef ?? null,
    newPaysEtranger: values.paysEtranger ?? null,
    newPlaqueImmatriculation: values.plaqueImmatriculation ?? null,
    comment: values.comment ?? null,
    newEtatIdCorrelation: isAdminFonctionnel
      ? values.idCorrelationEtatVehicule ?? null
      : null,
    commentProlongationMef: values.commentProlongationMef ?? null,
    dateProlongationMef: values.dateProlongationMef ?? null,
  };
};

export function isInstanceOfAdminFonctionnel(
  controller: ControllerApi,
): controller is AdminFonctionnelControllerApi {
  return controller instanceof AdminFonctionnelControllerApi;
}

export function isInstanceOfForceOrdre(
  controller: ControllerApi,
): controller is ForceOrdreControllerApi {
  return controller instanceof ForceOrdreControllerApi;
}

function isInstanceOfAutoriteFourriere(
  controller: ControllerApi,
): controller is AutoriteFourriereControllerApi {
  return controller instanceof AutoriteFourriereControllerApi;
}

function getUpdateDonnesVehicule(controller: ControllerApi) {
  if (isInstanceOfAdminFonctionnel(controller)) {
    return controller.updateDonneesInterieurVehiculeUsingPOST.bind(controller);
  }
  if (isInstanceOfAutoriteFourriere(controller)) {
    return controller.updateDonneesInterieurVehiculeForNotAdminUsingPOST1.bind(
      controller,
    );
  }
  return controller.updateDonneesInterieurVehiculeForNotAdminUsingPOST.bind(
    controller,
  );
}

function getValidateUpdateDonneesVehiculeField(controller: ControllerApi) {
  if (isInstanceOfAdminFonctionnel(controller)) {
    return controller.validateUpdateDonneesInterieurVehiculeFieldUsingPOST.bind(
      controller,
    );
  }
  if (isInstanceOfForceOrdre(controller)) {
    return controller.validateUpdateDonneesInterieurVehiculeFieldForNotAdminUsingPOST.bind(
      controller,
    );
  }
  return controller.validateUpdateDonneesInterieurVehiculeFieldForNotAdminUsingPOST1.bind(
    controller,
  );
}

function useSubmitUpdateDonneesVehicule(
  controller: ControllerApi,
  dossierId: string,
  setDossier: SetDossierFo | SetDossierAdminF,
  closeModal: () => void,
): SubmitFunction {
  const [inProgress, setInProgress] = useState<boolean>(false);

  const isAdminFonctionnel = isInstanceOfAdminFonctionnel(controller);

  const validateUpdateDonneesVehicule =
    getValidateUpdateDonneesVehiculeField(controller);

  return {
    submit: (values: UpdateDonnesVehiculeFormValues) => {
      setInProgress(true);

      const updatedDatas: UpdateDonneesInterieurVehiculeRequestDto =
        buildUpdateDonneesVehiculeDto(values, controller);

      return getUpdateDonnesVehicule(controller)(dossierId, updatedDatas)
        .then(
          setDossier as (
            dossier: DossierDtoAdminFonctionnel | DossierResponseDto,
          ) => void,
        )
        .then(closeModal)
        .catch((error: Response) => {
          void backAlertMessage(error);
        })
        .finally(() => {
          setInProgress(false);
        });
    },
    inProgress: inProgress,
    validateUpdateDonneesVehicule,
    isAdminFonctionnel,
  };
}

export default useSubmitUpdateDonneesVehicule;
